/**
 * Shared visual language for the three submission forms
 * (Event / Review / Coupon).
 *
 * IMPORTANT: this module is PRESENTATION ONLY. It must never contain
 * validation, submission or state logic — the forms own all of that.
 * Anything exported here is either an `sx` object or a dumb presentational
 * component, so restyling the forms can never change their behaviour.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

/* ------------------------------------------------------------------ */
/* Design tokens — aligned with the mobile bottom-nav brand gradient   */
/* ------------------------------------------------------------------ */
export const FORM_TOKENS = {
  brand: '#6d4cff',
  brandDark: '#5a35f0',
  brandSoft: 'rgba(109, 76, 255, 0.08)',
  gradient: 'linear-gradient(135deg, #6d4cff 0%, #c044ff 100%)',
  pageBg: '#f6f7fb',
  surface: '#ffffff',
  border: '#e6e8ef',
  borderStrong: '#d3d8e3',
  text: '#1f2937',
  muted: '#6b7280'
};

/* ------------------------------------------------------------------ */
/* Page shell                                                          */
/* ------------------------------------------------------------------ */

/** Outermost wrapper of a form page. `embedded` = rendered inside the
 *  mobile full-screen sheet (the sheet already owns the page chrome). */
export const pageShellSx = (embedded = false) => ({
  backgroundColor: embedded ? 'transparent' : FORM_TOKENS.pageBg,
  py: embedded ? 0 : { xs: 2, md: 5 },
  px: embedded ? 0 : { xs: 0, md: 0 },
  minHeight: embedded ? 'auto' : '100vh',
  overflowY: 'auto',
  color: FORM_TOKENS.text
});

/** The card that holds the whole form. */
export const formCardSx = (embedded = false) => ({
  p: embedded ? { xs: 2, sm: 2.5 } : { xs: 2.5, sm: 4 },
  borderRadius: embedded ? 3 : { xs: 3, sm: 4 },
  backgroundColor: FORM_TOKENS.surface,
  border: `1px solid ${FORM_TOKENS.border}`,
  boxShadow: embedded
    ? 'none'
    : '0 10px 30px rgba(31, 41, 55, 0.06), 0 2px 6px rgba(31, 41, 55, 0.04)',
  height: 'auto',
  overflow: 'visible'
});

/**
 * Field-level polish applied ONCE on the form card, so every input inside
 * inherits it. No individual field markup has to change.
 */
export const fieldsSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fcfcfe',
    transition: 'box-shadow .18s ease, background-color .18s ease',
    '& fieldset': { borderColor: FORM_TOKENS.border },
    '&:hover fieldset': { borderColor: FORM_TOKENS.borderStrong },
    '&.Mui-focused': {
      backgroundColor: FORM_TOKENS.surface,
      boxShadow: `0 0 0 4px ${FORM_TOKENS.brandSoft}`
    },
    '&.Mui-focused fieldset': {
      borderColor: FORM_TOKENS.brand,
      borderWidth: '1.5px'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: FORM_TOKENS.brand },
  '& .MuiFormHelperText-root': {
    marginLeft: '2px',
    fontSize: '0.75rem',
    color: FORM_TOKENS.muted
  },
  '& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked': {
    color: FORM_TOKENS.brand
  },
  // Keep inputs at >=16px on mobile so iOS Safari never zooms the viewport.
  '@media (max-width: 767px)': {
    '& .MuiOutlinedInput-input': { fontSize: '16px' }
  }
};

/* ------------------------------------------------------------------ */
/* Page header (title + subtitle + status chips)                       */
/* ------------------------------------------------------------------ */
export const pageHeaderSx = {
  textAlign: 'center',
  mb: 3,
  pb: 2.5,
  borderBottom: `1px solid ${FORM_TOKENS.border}`
};

export const pageTitleSx = {
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: FORM_TOKENS.text,
  fontSize: { xs: '1.35rem', sm: '1.9rem' },
  mb: 0.75
};

/* ------------------------------------------------------------------ */
/* Section heading                                                     */
/* ------------------------------------------------------------------ */

/**
 * A section title with a small brand accent bar. Purely decorative —
 * drop-in replacement for the old `<Typography variant="h6">` headings.
 */
export const SectionHeading = ({ children, caption, sx = {} }) => (
  <Box sx={{ mb: 2, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        sx={{
          width: 4,
          height: 18,
          borderRadius: 2,
          background: FORM_TOKENS.gradient,
          flexShrink: 0
        }}
      />
      <Typography
        component="h2"
        sx={{
          fontSize: { xs: '1rem', sm: '1.05rem' },
          fontWeight: 700,
          color: FORM_TOKENS.text,
          letterSpacing: '-0.005em'
        }}
      >
        {children}
      </Typography>
    </Box>
    {caption && (
      <Typography
        sx={{ mt: 0.5, ml: '17px', fontSize: '0.8rem', color: FORM_TOKENS.muted }}
      >
        {caption}
      </Typography>
    )}
  </Box>
);

/** Groups a section's fields in a soft panel to separate it visually. */
export const sectionPanelSx = {
  mb: 3,
  p: { xs: 1.75, sm: 2.5 },
  borderRadius: 3,
  border: `1px solid ${FORM_TOKENS.border}`,
  backgroundColor: '#fbfbfe'
};

/* ------------------------------------------------------------------ */
/* Submit button                                                       */
/* ------------------------------------------------------------------ */
export const submitButtonSx = {
  px: 4,
  py: 1.4,
  width: { xs: '100%', sm: 'auto' },
  minWidth: { sm: 260 },
  fontSize: '1rem',
  fontWeight: 700,
  borderRadius: 999,
  textTransform: 'none',
  color: '#fff',
  background: FORM_TOKENS.gradient,
  boxShadow: '0 10px 24px rgba(109, 76, 255, 0.28)',
  '&:hover': {
    background: FORM_TOKENS.gradient,
    filter: 'brightness(0.96)',
    boxShadow: '0 12px 26px rgba(109, 76, 255, 0.34)'
  }
};

export const dividerSx = { my: 3, borderColor: FORM_TOKENS.border };
